// Copyright (c) 2024 by Audere

import {
  State as AuthState,
  selectCurrentEmail,
  selectCurrentToken,
  setCredentials,
} from "../features/slices/authSlice";
import { Box, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { createStyleSheet, globalStyles, logoColors } from "../utils/styles";

import AppLogo from "../../assets/audere-africa-logo.png";
import LoginButton from "../components/LoginButton";
import { WarningAlert } from "../components/Alerts";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { usesStagingApi } from "../utils/config";
import { useAppDispatch } from "../features/store";

interface LocationState {
  from?: { pathname: string };
}

const getGoogleClientID = () => {
  const clientId = usesStagingApi()
    ? "616282600491-ul41ks5q58uu0adt6lmuk911al0rlsif.apps.googleusercontent.com"
    : "392407402173-5apgdk96qnv2cb3fih6vna7armr457bl.apps.googleusercontent.com";
  return clientId;
};

const Login: React.FC = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const [welcomeMsg, setWelcomeMsg] = useState("Welcome");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const clientId = getGoogleClientID();
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const userEmail = useSelector(selectCurrentEmail);

  const locationState = useLocation().state as LocationState | undefined;
  const destination = locationState?.from?.pathname ?? "/label-type-select";

  useEffect(() => {
    const isReturningUser = localStorage.getItem("isReturningUser");
    if (isReturningUser) setWelcomeMsg("Welcome back!");
    if (isLoggedIn && userEmail) navigate(destination, { replace: true });
  }, [isLoggedIn, userEmail, navigate, destination]);

  const loginSuccess = useCallback(
    (authData: AuthState) => {
      dispatch(setCredentials(authData));
      localStorage.setItem("isReturningUser", "true");
    },
    [dispatch]
  );

  const loginError = useCallback((errorMsg: string) => {
    setErrorMsg(errorMsg);
    console.error("Login Failed: " + errorMsg);
  }, []);

  return (
    <Box sx={globalStyles.pageContainerRow}>
      {/* Left pane with title */}
      <Box sx={styles.leftPane}>
        <Box textAlign="center">
          <Typography variant="h2" sx={styles.title}>
            Text Labeler
          </Typography>
        </Box>
      </Box>

      {/* Right pane with login form */}
      <Box sx={styles.rightPane}>
        {/* Logo */}
        <Box marginBottom={2}>
          <img src={AppLogo} alt="HealthPulse logo" style={{ height: 60 }} />
        </Box>

        {/* Greeting and login */}
        <Stack spacing={2} alignItems="center">
          <Typography variant="h3">{welcomeMsg}</Typography>
          <Typography>Please sign in with Google to continue.</Typography>
          <LoginButton
            googleClientId={clientId}
            onAuthSuccess={loginSuccess}
            onAuthError={loginError}
          />
          {errorMsg && <WarningAlert message={errorMsg} />}
        </Stack>
      </Box>
    </Box>
  );
};

const styles = createStyleSheet({
  leftPane: {
    alignItems: "center",
    backgroundColor: logoColors.continent,
    display: "flex",
    flex: 7,
    justifyContent: "center",
  },
  title: {
    borderBottom: "2px solid white",
    color: "white",
    paddingBottom: 1,
  },
  rightPane: {
    ...globalStyles.flexColumn,
    alignItems: "center",
    flex: 5,
    justifyContent: "center",
    padding: 4,
  },
});

export default Login;
